import { Box, Flex, Spinner, Text } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router";
import { GET_NEWS_DETAIL_QUERY_KEY, getNewsDetail } from "./api/newsScreenApi";
import { HtmlParser } from "../root/HtmlParser";
import { useIsSmallBreakpoint } from "../../utils/useBreakpoints";
import { Reactions } from "./components/Reactions";
import { RelatedNews } from "./components/RelatedNews";
import { SectionTitle } from "../../components/text/SectionTitle";
import { t } from "i18next";
import { useAccountData } from "../../hooks/useAccountData";
import { BreadcrumbNav } from "../../components/navigation/components/BreadcrumbNav";
import { NewsDetailsComments } from "./NewsDetailsComments";
import { publishVideoStartEvent } from "../root/api/eventsApi";
import { YoutubeIframePlayer } from "./components/YoutubeIframePlayer";
import { NewsDetailsHeader } from "./components/NewsDetailsHeader";
import { NewsTags } from "./components/NewsTags";
import { useUserData } from "../../hooks/useUserData";

type RouteParams = {
  id: string;
  title: string;
};

export default function NewsDetailsScreen() {
  const accountData = useAccountData();
  const { userData } = useUserData();
  const isSmallBreakpoint = useIsSmallBreakpoint();
  const disableCommentingFlag = accountData?.features["disable_commenting"];

  const params = useParams<RouteParams>();

  const { isLoading, data } = useQuery({
    enabled: !!accountData && !!params.id,
    queryKey: [...GET_NEWS_DETAIL_QUERY_KEY, params.id, accountData?.user?.id],
    queryFn: () => getNewsDetail(params.id ?? ""),
    refetchOnWindowFocus: false,
  });

  const article = data?.data.payload?.results;
  const isVideoHub = article?.category?.slug === "video-hub";

  const areCommentsDisabled =
    !(article?.is_open ?? false) ||
    (!!disableCommentingFlag && !userData?.gold);

  if (isLoading || !accountData) {
    return (
      <Flex width="full" alignItems="center" justifyContent="center">
        <Spinner color="blaze.blaze" />
      </Flex>
    );
  }

  if (!article) {
    return (
      <Flex width="full" px={3}>
        <Text color="neutral.white">{t("screens.hub.errorTitle")}</Text>
      </Flex>
    );
  }

  const sendVideoStartedEvent = (videoUrl: string) => {
    const category = article.category?.slug;
    if (category) {
      publishVideoStartEvent(category, videoUrl, article.id);
    }
  };

  // Determine if the article body actually has any content
  const bodyAsHtml = document.createElement("div");
  bodyAsHtml.innerHTML = article.body ?? "";
  const hasBody = bodyAsHtml.innerText.trim().length > 0;

  const breadcrumbs = isVideoHub
    ? [
        { name: t("screens.hub.videos.title"), url: "/watch" },
        { name: article.title, url: window.location.pathname },
      ]
    : [
        { name: t("screens.hub.news.title"), url: "/news" },
        {
          name: article.category?.display_name ?? "",
          url: `/news/${article.category?.slug}`,
        },
        { name: article.title, url: window.location.pathname },
      ];

  return (
    <Flex overflowY="auto" maxHeight="100%" width="100%">
      <Flex
        flexDirection="column"
        maxWidth={isSmallBreakpoint ? "100%" : "1200px"}
        width="100%"
        margin="0 auto"
        pt="32px"
        px="12px"
        pr="24px"
        gap="40px"
      >
        {isVideoHub ? (
          <>
            <BreadcrumbNav path={breadcrumbs} />
            <Box transform="translateY(0px)">
              <YoutubeIframePlayer onFirstPlay={sendVideoStartedEvent}>
                <iframe
                  style={{ borderRadius: "24px", aspectRatio: "16/9" }}
                  width="100%"
                  src={`https://www.youtube.com/embed/${article.youtube_id}`}
                  title="YouTube video player"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </YoutubeIframePlayer>
            </Box>
          </>
        ) : null}
        <Flex
          width="full"
          flexDirection={isSmallBreakpoint ? "column" : "row"}
          justifyContent={isSmallBreakpoint ? "flex-start" : "center"}
          gap="40px"
        >
          <Flex gap={2} direction="column" flexGrow={1} minWidth="0" flex="1">
            {isVideoHub ? null : (
              <Box pb="8px">
                <BreadcrumbNav path={breadcrumbs} />
              </Box>
            )}
            <NewsDetailsHeader article={article} />
            <Flex direction="column" gap="40px" pb="42px">
              {hasBody && (
                <HtmlParser
                  htmlString={article.parsed_body ?? ""}
                  onVideoFirstPlay={sendVideoStartedEvent}
                />
              )}

              {Object.keys(article.tags ?? {}).length > 0 && (
                <>
                  <Box h="1px" w="full" background="transparent.white.10" />
                  <NewsTags article={article} />
                  <Box h="1px" w="full" background="transparent.white.10" />
                </>
              )}
              <Reactions
                articleId={article.id}
                reactions={article.reactions ?? []}
              />
              {!isVideoHub && (
                <>
                  <Box h="1px" w="full" background="transparent.white.10" />
                  <SectionTitle fontSize="32px" fontColor="dune.dune">
                    {isVideoHub
                      ? t("screens.hub.videos.relatedTitle")
                      : t("screens.hub.news.relatedTitle")}
                  </SectionTitle>
                  <RelatedNews
                    articleId={article.id}
                    cardSize="s"
                    maxWidth="100%"
                    gap="40px"
                  />
                </>
              )}
              <Box h="1px" w="full" background="transparent.white.10" />
              <NewsDetailsComments
                articleId={article.id}
                areCommentsDisabled={areCommentsDisabled}
              />
            </Flex>
          </Flex>
          {!isSmallBreakpoint ? (
            <Flex
              direction="column"
              gap="32px"
              minWidth="235px"
              maxWidth="235px"
              position="relative"
              pb="48px"
            >
              <SectionTitle fontSize="18px" fontColor="dune.dune">
                {isVideoHub
                  ? t("screens.hub.videos.relatedTitle")
                  : t("screens.hub.news.relatedTitle")}
              </SectionTitle>
              <RelatedNews
                articleId={article.id}
                cardSize={isSmallBreakpoint ? "xs" : "m"}
                showDividers
              />
            </Flex>
          ) : null}
        </Flex>
      </Flex>
    </Flex>
  );
}
